import React, { useState } from 'react';
import { Trash2, Mail, Shield, Eye, Edit3, Crown, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface AccessControlProps {
  event: any;
  eventId: string;
}

export default function AccessControl({ event, eventId }: AccessControlProps) {
  const { user } = useAuth();
  const [invitations, setInvitations] = useState<any[]>([]);
  const [accessList, setAccessList] = useState<any[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'viewer' | 'editor' | 'admin'>('viewer');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteAccessId, setDeleteAccessId] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  React.useEffect(() => {
    loadUserAccess();
  }, [eventId]);

  const loadUserAccess = async () => {
    const [invitesRes, accessRes] = await Promise.all([
      supabase
        .from('event_invitations')
        .select('*')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false }),
      supabase
        .from('event_access')
        .select(`
          *,
          profiles:user_id(email, full_name)
        `)
        .eq('event_id', eventId)
        .order('created_at', { ascending: false })
    ]);

    if (invitesRes.data) setInvitations(invitesRes.data);
    if (accessRes.data) setAccessList(accessRes.data);
  };

  const sendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviting(true);
    setInviteError(null);
    setInviteSuccess(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/event-invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: inviteEmail,
          role: inviteRole,
          event_id: eventId,
          event_name: event.name,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send invitation');
      }

      const successMessage = result.type === 'direct_access'
        ? `Access granted to ${inviteEmail} - they already had an account!`
        : `Invitation sent to ${inviteEmail}`;

      setInviteSuccess(successMessage);
      setInviteEmail('');
      setInviteRole('viewer');
      loadUserAccess();

      setTimeout(() => setInviteSuccess(null), 5000);
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      setInviteError(error.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const deleteInvitation = async (id: string) => {
    await supabase.from('event_invitations').delete().eq('id', id);
    setDeleteId(null);
    loadUserAccess();
  };

  const deleteAccess = async (id: string) => {
    await supabase.from('event_access').delete().eq('id', id);
    setDeleteAccessId(null);
    loadUserAccess();
  };

  const updateAccessRole = async (accessId: string, newRole: string) => {
    await supabase
      .from('event_access')
      .update({ role: newRole })
      .eq('id', accessId);
    loadUserAccess();
  };

  const roleInfo = {
    viewer: { icon: Eye, label: 'Viewer', color: 'text-zinc-400', description: 'Can view all sections' },
    editor: { icon: Edit3, label: 'Editor', color: 'text-primary-400', description: 'Can view and edit' },
    admin: { icon: Crown, label: 'Admin', color: 'text-amber-400', description: 'Full control + manage access' }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-2">Access & Sharing</h2>
        <p className="text-zinc-500">Invite team members to collaborate on this event</p>
      </div>
          <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-6">
            <h3 className="text-lg font-medium text-white mb-4">Invite Team Member</h3>
            <form onSubmit={sendInvitation} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="email" className="text-zinc-300">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@example.com"
                    required
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="role" className="text-zinc-300">Permission Level</Label>
                  <Select value={inviteRole} onValueChange={(value: any) => setInviteRole(value)}>
                    <SelectTrigger className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {Object.entries(roleInfo).map(([key, info]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <info.icon className={`w-4 h-4 ${info.color}`} />
                            {info.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {inviteError && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  {inviteError}
                </div>
              )}
              {inviteSuccess && (
                <div className="text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                  {inviteSuccess}
                </div>
              )}
              <Button type="submit" disabled={inviting} className="bg-white text-zinc-900 hover:bg-zinc-200">
                <UserPlus className="w-4 h-4 mr-2" />
                {inviting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </form>

            <div className="mt-6 p-4 bg-zinc-800/30 rounded-lg border border-zinc-800/50">
              <h4 className="text-sm font-medium text-white mb-3">Permission Levels</h4>
              <div className="space-y-2">
                {Object.entries(roleInfo).map(([key, info]) => (
                  <div key={key} className="flex items-start gap-3">
                    <info.icon className={`w-4 h-4 mt-0.5 ${info.color}`} />
                    <div>
                      <div className="text-sm font-medium text-white">{info.label}</div>
                      <div className="text-xs text-zinc-500">{info.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-white mb-4">Current Access</h3>
            {accessList.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-6 text-center">
                <p className="text-zinc-500">No users have access yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {accessList.map((access) => {
                  const RoleIcon = roleInfo[access.role as keyof typeof roleInfo].icon;
                  const roleColor = roleInfo[access.role as keyof typeof roleInfo].color;

                  return (
                    <div key={access.id} className="bg-zinc-900/50 border border-zinc-800/50 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                          <Shield className="w-5 h-5 text-zinc-400" />
                        </div>
                        <div>
                          <div className="text-white font-medium">{access.profiles?.full_name || access.profiles?.email}</div>
                          <div className="text-sm text-zinc-500">{access.profiles?.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Select
                          value={access.role}
                          onValueChange={(value) => updateAccessRole(access.id, value)}
                        >
                          <SelectTrigger className="w-32 bg-zinc-800/50 border-zinc-700 text-white h-9">
                            <div className="flex items-center gap-2">
                              <RoleIcon className={`w-3.5 h-3.5 ${roleColor}`} />
                              <SelectValue />
                            </div>
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {Object.entries(roleInfo).map(([key, info]) => (
                              <SelectItem key={key} value={key}>
                                <div className="flex items-center gap-2">
                                  <info.icon className={`w-4 h-4 ${info.color}`} />
                                  {info.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteAccessId(access.id)}
                          className="text-zinc-400 hover:text-red-400 h-9 w-9"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {invitations.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-white mb-4">Pending Invitations</h3>
              <div className="space-y-2">
                {invitations.map((invite) => {
                  const RoleIcon = roleInfo[invite.role as keyof typeof roleInfo].icon;
                  const roleColor = roleInfo[invite.role as keyof typeof roleInfo].color;

                  return (
                    <div key={invite.id} className="bg-zinc-900/50 border border-zinc-800/50 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                          <Mail className="w-5 h-5 text-zinc-400" />
                        </div>
                        <div>
                          <div className="text-white font-medium">{invite.email}</div>
                          <div className="text-sm text-zinc-500">
                            {invite.status === 'pending' ? 'Invitation sent' : invite.status}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-sm">
                          <RoleIcon className={`w-4 h-4 ${roleColor}`} />
                          <span className="text-zinc-300">{roleInfo[invite.role as keyof typeof roleInfo].label}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteId(invite.id)}
                          className="text-zinc-400 hover:text-red-400 h-9 w-9"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Cancel Invitation?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will remove the pending invitation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)} className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteInvitation(deleteId)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteAccessId} onOpenChange={() => setDeleteAccessId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Remove Access?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This user will no longer be able to access this event.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteAccessId(null)} className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteAccessId && deleteAccess(deleteAccessId)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
